import { validateCustomEndpoint } from '@parcel/remote-mcp/custom-endpoint';
import { CUSTOM_CONNECTION_ACCESS_NOTE } from '@parcel/tools';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@parcel/ui';
import { TriangleAlert } from 'lucide-react';
import * as React from 'react';
import { apiErrorCode, apiErrorMessage } from '../../lib/api.js';
import { createCustomConnection, followConnectionRedirect } from '../../lib/spec-connections.js';

export interface CustomMcpDialogProps {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Only for a server that needed no redirect; an OAuth one leaves the page instead. */
  onCreated: () => void;
}

const UNAUTHENTICATED_WARNING =
  'This server asks nobody to sign in, so anyone who knows its address can use it. Connect it only if that is what you expect.';

const ACKNOWLEDGE_LABEL = 'Connect this server even though it asks nobody to sign in';

/** Connect a custom MCP server by name and URL. Two fields, and no third: authorization is
    discovered from the server itself, so there is nowhere here for a credential to be typed. */
export function CustomMcpDialog({
  workspaceId,
  open,
  onOpenChange,
  onCreated,
}: CustomMcpDialogProps) {
  const [name, setName] = React.useState('');
  const [url, setUrl] = React.useState('');
  const [urlError, setUrlError] = React.useState<string | null>(null);
  const [formError, setFormError] = React.useState<string | null>(null);
  const [needsAcknowledgement, setNeedsAcknowledgement] = React.useState(false);
  const [acknowledged, setAcknowledged] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  // A ref, not state: two clicks batched into the same render both read stale state, but a ref
  // mutates immediately, so the second one sees the first one's lock.
  const inFlightRef = React.useRef(false);

  React.useEffect(() => {
    if (open) return;
    setName('');
    setUrl('');
    setUrlError(null);
    setFormError(null);
    setNeedsAcknowledgement(false);
    setAcknowledged(false);
  }, [open]);

  // An acknowledgement is about one server: editing the address discards it rather than
  // carrying a decision about the old address onto a new one.
  const handleUrlChange = (value: string) => {
    setUrl(value);
    setUrlError(null);
    setNeedsAcknowledgement(false);
    setAcknowledged(false);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const validated = validateCustomEndpoint(url);
    if (!validated.ok) {
      setUrlError(validated.reason);
      return;
    }
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setSubmitting(true);
    setUrlError(null);
    setFormError(null);
    let redirecting = false;
    try {
      const result = await createCustomConnection(workspaceId, {
        name: name.trim(),
        url: validated.url,
        ...(acknowledged ? { acknowledgeUnauthenticated: true } : {}),
      });
      // A full-page redirect to the server's own consent screen: the browser is about to
      // leave, so there is nothing left here to close or refetch.
      if (result.redirectUrl) {
        redirecting = true;
        followConnectionRedirect(result.redirectUrl);
        return;
      }
      onOpenChange(false);
      onCreated();
    } catch (err) {
      if (apiErrorCode(err) === 'unauthenticated_server_not_acknowledged') {
        setNeedsAcknowledgement(true);
      } else {
        setFormError(apiErrorMessage(err, 'Unable to connect this server.'));
      }
    } finally {
      // Navigation is not instant, so the lock outlives this handler rather than reopening
      // Connect for the gap before the browser leaves: a second submit is a second connection.
      if (!redirecting) {
        inFlightRef.current = false;
        setSubmitting(false);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="custom-mcp-dialog">
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Add a custom connector</DialogTitle>
            <DialogDescription>
              Connect a remote MCP server this workspace runs or trusts. Parcel reads the tools it
              offers; an owner or admin reviews each one before Spec can use it.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="custom-mcp-name">Name</Label>
            <Input
              id="custom-mcp-name"
              value={name}
              maxLength={120}
              placeholder="e.g. Acme internal tools"
              onChange={(event) => setName(event.target.value)}
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="custom-mcp-url">Remote MCP server URL</Label>
            <Input
              id="custom-mcp-url"
              value={url}
              maxLength={2048}
              placeholder="https://mcp.example.com/mcp"
              onChange={(event) => handleUrlChange(event.target.value)}
            />
            {urlError ? <p className="text-sm text-destructive">{urlError}</p> : null}
          </div>

          <p className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
            {CUSTOM_CONNECTION_ACCESS_NOTE}
          </p>

          {needsAcknowledgement ? (
            <div
              data-testid="custom-mcp-unauthenticated-warning"
              className="space-y-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              <p className="flex items-start gap-2">
                <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
                {UNAUTHENTICATED_WARNING}
              </p>
              {/* A span, not a label: Checkbox renders a button, which htmlFor cannot name.
                  Its own aria-label carries the same words. */}
              <span className="flex items-center gap-2 font-medium">
                <Checkbox
                  checked={acknowledged}
                  aria-label={ACKNOWLEDGE_LABEL}
                  onCheckedChange={(checked) => setAcknowledged(checked === true)}
                />
                {ACKNOWLEDGE_LABEL}
              </span>
            </div>
          ) : null}

          {formError ? <p className="text-sm text-destructive">{formError}</p> : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                submitting || !name.trim() || !url.trim() || (needsAcknowledgement && !acknowledged)
              }
            >
              {submitting ? 'Connecting…' : 'Connect'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
